import { Component, computed, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { GroupDescriptor, GroupResult, process } from '@progress/kendo-data-query';
import {
  ErrorComponent, FormFieldComponent, RadioButtonComponent, TextBoxComponent
} from '@progress/kendo-angular-inputs';
import { CustomMessagesComponent, LabelComponent } from '@progress/kendo-angular-label';
import { ComboBoxComponent } from '@progress/kendo-angular-dropdowns';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ControlNamePipe } from 'src/app/pipes/control-name.pipe';
import { KENDO_ADORNMENTS } from '@progress/kendo-angular-common';
import { Icons } from 'src/app/icons';
import { TranslatePipe } from '@ngx-translate/core';
import { Category } from 'src/app/enum/category.enum';
import { SVGIconComponent } from '@progress/kendo-angular-icons';
import { LowerCasePipe } from '@angular/common';
import { SpacerComponent } from 'src/app/ui/atoms/spacer/spacer.component';
import { TestDataService } from 'src/app/services/testData.service';
import { Subscription } from 'rxjs';
import { mockData } from 'src/app/mock/data';
import { Utils } from 'src/app/utils';
import { FormService } from 'src/app/services/form.service';
import { COMMON_COUNTRIES } from 'src/app/app.constants';

@Component({
    selector: 'app-invoice-data',
    imports: [
        FormFieldComponent,
        ErrorComponent,
        ComboBoxComponent,
        ReactiveFormsModule,
        TextBoxComponent,
        ControlNamePipe,
        KENDO_ADORNMENTS,
        LabelComponent,
        RadioButtonComponent,
        TranslatePipe,
        CustomMessagesComponent,
        SVGIconComponent,
        LowerCasePipe,
        SpacerComponent,
    ],
    templateUrl: './invoice-data.component.html',
    styleUrl: './invoice-data.component.scss'
})
export class InvoiceDataComponent implements OnInit, OnDestroy {
  private testDataService = inject(TestDataService);
  private testDataSubscription: Subscription;

  private formService = inject(FormService);

  readonly icons = Icons;
  readonly categories = Category;

  @Input({required: true}) formId: string;

  formGroup: FormGroup;

  countries = computed(() => this.formService.countries().map((d: any, index: number) => ({
    ...d,
    id: index,
    group: COMMON_COUNTRIES.includes(d.code) ? '---------' : '---------------',
  })));

  groupedCountries: GroupResult[] = this.groupData(this.countries())
  filteredGroupedCountries: GroupResult[] = [...this.groupedCountries];

  constructor() {
    this.formGroup = new FormGroup({
      category: new FormControl({value: null, disabled: false}, [Validators.required, Validators.minLength(2)]),
      country: new FormControl({value: null, disabled: false}, [Validators.required, Validators.minLength(2)]),
      firstName: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      lastName: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      companyName: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      taxNumber: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      vatNumber: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      streetName: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      streetNumber: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(1)]),
      village: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
      postalCode: new FormControl({value: null, disabled: true}, [Validators.required, Validators.minLength(2)]),
    });
  }

  private groupData(data: any[]): GroupResult[] {
    if (!data || data.length === 0) return [];
    const groups: GroupDescriptor[] = [{field: 'group'}];
    const result = process(data, {group: groups});
    return (result && Array.isArray((result as any).data)) ? (result as any).data : [];
  }

  ngOnInit() {
    this.initForm();
    this.formGroup.valueChanges.subscribe((values) => {
      this.checkTaxNumber(values);
      this.buildInvoiceData();
    });
    this.initTestDataObserver();
  }

  private initTestDataObserver() {
    this.testDataSubscription = this.testDataService.fillDataObserver.subscribe(() => {
      if (document.getElementById(this.formId)) {
        this.formGroup.get('category')?.setValue(
          Utils.getRandomElement(Object.keys(this.categories)).toString().toLowerCase()
        );
        this.categoryOrCountryChanged();
        this.formGroup.get('firstName')?.setValue(Utils.getRandomElement(mockData.names));
        this.formGroup.get('lastName')?.setValue(Utils.getRandomElement(mockData.lastNames));
        this.formGroup.get('companyName')?.setValue(Utils.getRandomElement(mockData.companyNames));
        this.formGroup.get('taxNumber')?.setValue(Utils.getRandomElement(mockData.taxNumbers));
        this.formGroup.get('vatNumber')?.setValue(Utils.getRandomElement(mockData.vatNumbers));
        this.formGroup.get('streetName')?.setValue(Utils.getRandomElement(mockData.streetNames));
        this.formGroup.get('streetNumber')?.setValue(Utils.getRandomElement(
          Array.from({length: 999}, (_, i) => i + 1))
        );
        this.formGroup.get('village')?.setValue(Utils.getRandomElement(mockData.cities));
        this.formGroup.get('postalCode')?.setValue(Utils.getRandomElement(mockData.zipCodes));
        this.formGroup.markAllAsTouched();
      }
    });
  }

  private initForm() {
    const countryCode = this.formService.form().invoice?.country?.code || this.formService.params['dialCode'];
    const codeItem = this.countries().find(c => c.code === countryCode);
    this.formGroup.get('country')?.setValue(codeItem);

    const category = this.formService.form().invoice?.category;
    this.formGroup.get('category')?.setValue(category);

    this.categoryOrCountryChanged();

    this.formGroup.get('streetName')?.setValue(this.formService.form().invoice?.streetName || '');
    this.formGroup.get('streetNumber')?.setValue(this.formService.form().invoice?.streetNumber || '');
    this.formGroup.get('village')?.setValue(this.formService.form().invoice?.village || '');
    this.formGroup.get('postalCode')?.setValue(this.formService.form().invoice?.postalCode || '');

    if (this.formService.form().invoice?.country?.code === 'IT') {
      this.formGroup.get('taxNumber')?.setValue(this.formService.form().invoice?.taxNumber || '');
    }

    if (category === Category.INDIVIDUAL) {
      this.formGroup.get('firstName')?.setValue(this.formService.form().invoice?.firstName || '');
      this.formGroup.get('lastName')?.setValue(this.formService.form().invoice?.lastName || '');
    } else if (category === Category.BUSINESS) {
      this.formGroup.get('companyName')?.setValue(this.formService.form().invoice?.companyName || '');
      this.formGroup.get('vatNumber')?.setValue(this.formService.form().invoice?.vatNumber || '');
    }
  }

  private checkTaxNumber(values: any) {
    if (!values?.category || !values?.taxNumber || values.taxNumber.length === 0) return;
    if (values?.country?.code !== 'IT') return;
    if (values.category === Category.INDIVIDUAL && values.taxNumber?.length !== 16) {

      this.formGroup.get('taxNumber')?.setErrors({invalidFormat: true}, {emitEvent: false})
    } else if (values.category === Category.BUSINESS && values.taxNumber?.length < 11) {
      this.formGroup.get('taxNumber')?.setErrors({invalidFormat: true}, {emitEvent: false})
    }
  }

  categoryOrCountryChanged() {
    const values: any = this.formGroup.getRawValue();
    if (!values.country|| !values.category) {
      this.formGroup.get('taxNumber')?.disable();
      this.formGroup.get('firstName')?.disable();
      this.formGroup.get('lastName')?.disable();
      this.formGroup.get('companyName')?.disable();
      this.formGroup.get('vatNumber')?.disable();
      this.formGroup.get('streetName')?.disable();
      this.formGroup.get('streetNumber')?.disable();
      this.formGroup.get('village')?.disable();
      this.formGroup.get('postalCode')?.disable();
    }
    if (values.country?.code && values.category) {
      if (values.country.code === 'IT') {
        this.formGroup.get('taxNumber')?.enable();
      }
      if (values.category === this.categories.INDIVIDUAL) {
        this.formGroup.get('firstName')?.enable();
        this.formGroup.get('lastName')?.enable();
        if (values.country?.code === 'IT') {
          this.formGroup.get('taxNumber')?.setValidators([Validators.required, Validators.minLength(2)]);
        }
      } else if (values.category === this.categories.BUSINESS) {
        if (values.country.code === 'IT') {
          this.formGroup.get('taxNumber')?.setErrors(null);
          this.formGroup.get('taxNumber')?.setValidators([Validators.minLength(2)]);
        }
        this.formGroup.get('companyName')?.enable();
        this.formGroup.get('vatNumber')?.enable();
      }
      this.formGroup.get('streetName')?.enable();
      this.formGroup.get('streetNumber')?.enable();
      this.formGroup.get('village')?.enable();
      this.formGroup.get('postalCode')?.enable();
    }
  }

  filterCountries(filter: string): void {
    if (!filter || filter.trim().length < 2) {
      this.filteredGroupedCountries = [...this.groupedCountries];
    } else {
      const filteredFlatList = this.countries().filter(
        country => country.name.toLowerCase().includes(filter.toLowerCase())
      );
      this.filteredGroupedCountries = this.groupData(filteredFlatList);
    }
  }

  private buildInvoiceData() {
    const values: any = this.formGroup.getRawValue();
    const invoice: any = {
      category: values?.category,
      country: values?.country,
      streetName: values?.streetName,
      streetNumber: values?.streetNumber,
      village: values?.village,
      postalCode: values?.postalCode,
    }

    if (invoice.country?.code === 'IT') {
      invoice.taxNumber = values?.taxNumber;
    }

    if (values.category === this.categories.INDIVIDUAL) {
      invoice.firstName = values?.firstName;
      invoice.lastName = values?.lastName;
    } else if (values.category === this.categories.BUSINESS) {
      invoice.companyName = values?.companyName;
      invoice.vatNumber = values?.vatNumber;
    }

    this.formService.update({
      ...this.formService.form(),
      invoice: invoice
    });
  }

  submit() {
    this.formGroup.markAllAsTouched();
    this.buildInvoiceData();
  }

  ngOnDestroy() {
    this.testDataSubscription?.unsubscribe();
  }

}
